import { useState, useEffect } from 'react';

/**
 * useResizable - Hook for resizable panel width with localStorage persistence
 * 
 * @param {Object} options
 * @param {string} options.storageKey - localStorage key for width
 * @param {string} options.hintStorageKey - localStorage key for hint seen
 * @param {number} options.defaultWidth - Default width in pixels
 * @param {number} options.minWidth - Minimum width in pixels
 * @param {number} options.maxWidthRatio - Maximum width as ratio of window (0-1)
 * @returns {Object} { width, isResizing, showHint, startResize, ResizeHandle }
 */
export default function useResizable({
  storageKey = 'panel-width',
  hintStorageKey = 'resize-hint-seen',
  defaultWidth = 380,
  minWidth = 300,
  maxWidthRatio = 0.5
} = {}) {
  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? parseInt(saved, 10) : defaultWidth;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [showHint, setShowHint] = useState(() => {
    return !localStorage.getItem(hintStorageKey);
  });

  // Handle resize drag
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = window.innerWidth - e.clientX;
      const maxWidth = window.innerWidth * maxWidthRatio;
      const clampedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
      setWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      localStorage.setItem(storageKey, width.toString());
      if (showHint) {
        setShowHint(false);
        localStorage.setItem(hintStorageKey, 'true');
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, width, showHint, storageKey, hintStorageKey, minWidth, maxWidthRatio]);

  const startResize = () => setIsResizing(true);

  return {
    width,
    isResizing,
    showHint,
    startResize
  };
}
